import React, { useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { useUniversities } from './hooks/useUniversities';
import { useUsers } from './hooks/useUsers';
import ErrorBoundary from './components/ui/ErrorBoundary';
import LoadingSpinner from './components/ui/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

const App: React.FC = () => {
  const auth = useAuth();
  const unis = useUniversities();
  const usrs = useUsers();

  useEffect(() => {
    Promise.all([unis.loadUniversities(), usrs.loadUsers()]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (auth.authLoading || (unis.loading && usrs.loading)) {
    return <LoadingSpinner />;
  }

  if (!auth.currentUser) {
    return (
      <LoginPage
        universities={unis.universities}
        users={usrs.users}
        loginMode={auth.loginMode}
        selectedUniversity={auth.selectedUniversity}
        email={auth.email}
        password={auth.password}
        error={auth.error}
        onLoginModeChange={auth.setLoginMode}
        onSelectedUniversityChange={auth.setSelectedUniversity}
        onEmailChange={auth.setEmail}
        onPasswordChange={auth.setPassword}
        onLogin={auth.handleLogin}
      />
    );
  }

  const filteredUsers = usrs.getFilteredUsers(auth.currentUser);

  const handleDeleteUniversity = async (id: string) => {
    await unis.handleDelete(id);
    // Remove associated users from local state
    await usrs.refreshUsers();
  };

  return (
    <DashboardPage
      currentUser={auth.currentUser}
      universities={unis.universities}
      filteredUsers={filteredUsers}
      onLogout={auth.handleLogout}
      // University operations
      showUniversityModal={unis.showModal}
      editingUniversity={unis.editingUniversity}
      onAddUniversity={unis.handleAdd}
      onEditUniversity={unis.handleEdit}
      onDeleteUniversity={handleDeleteUniversity}
      onOpenUniversityModal={unis.openModal}
      onCloseUniversityModal={unis.closeModal}
      // User operations
      showUserModal={usrs.showModal}
      editingUser={usrs.editingUser}
      onAddUser={(data) => usrs.handleAdd(data, auth.currentUser!)}
      onEditUser={(data) => usrs.handleEdit(data, auth.currentUser!)}
      onDeleteUser={usrs.handleDelete}
      onOpenUserModal={usrs.openModal}
      onCloseUserModal={usrs.closeModal}
      // Bulk upload
      onBulkUpload={(file) => usrs.handleBulkUpload(file, auth.currentUser!)}
      showBulkUploadResults={usrs.showBulkUploadResults}
      bulkUploadResults={usrs.bulkUploadResults}
      onCloseBulkUploadResults={usrs.closeBulkUploadResults}
    />
  );
};

const AppWithErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default AppWithErrorBoundary;
